import { Inject, Injectable } from "@angular/core";
import { PlatformService, WindowRef } from "@obg/ngx.global.evo";
import { castArray, includes, isEmpty } from "lodash-es";
import { combineLatest, EMPTY, Observable, of } from "rxjs";
import { mergeMap, switchMapTo } from "rxjs/operators";

import { GLOBAL_CONFIG } from "../const";
import { GlobalConfig } from "../model";

import { CacheStore } from "./cache-store";
import { PREFIX_DEFAULT, PREFIX_LANGUAGE, PREFIX_VERSION } from "./cache.const";
import { CacheFactory } from "./cache.factory";
import { CacheKey, StorageMode } from "./cache.model";

const CACHE_KEY_LANGUAGE = PREFIX_LANGUAGE;
const CACHE_KEY_VERSION = PREFIX_VERSION;

/**
 * Allows consumer to purge cache; i.e. remove empty keys and cache entries for different language and/or app versions.
 *
 * Currently only localstorage DIRECT purging is supported.
 *
 * todo - preferably we should find a way to purge all storages THROUGH third party library.
 *
 */
@Injectable({
	providedIn: "root",
})
export class CacheService {
	private appCache: CacheStore;

	constructor(
		@Inject(GLOBAL_CONFIG) private globalConfig: GlobalConfig,
		private cacheFactory: CacheFactory,
		private windowRef: WindowRef,
		private platformService: PlatformService
	) {
		this.appCache = this.cacheFactory.get("appCache", {
			storageMode: StorageMode.LocalStorage,
			maxAge: Number.MAX_VALUE,
		});
	}

	purge(): Observable<any> {
		if (this.platformService.isServer) {
			return EMPTY;
		}

		return combineLatest([
			this.appCache.getItemAsObservable<string>(CACHE_KEY_VERSION),
			this.appCache.getItemAsObservable<string>(CACHE_KEY_LANGUAGE),
		]).pipe(
			mergeMap(([siteVersion, languageCode]) => {
				const keysToPurge: CacheKey[] = [];
				const obs$: Observable<any>[] = [];

				if (siteVersion !== this.globalConfig.environmentContext.version) {
					const prefixedKeys = this.getAllByPrefix(PREFIX_VERSION);
					keysToPurge.push(...prefixedKeys);
					obs$.push(
						this.appCache.setItemAsObservable(
							CACHE_KEY_VERSION,
							this.globalConfig.environmentContext.version
						)
					);
				}

				if (languageCode !== this.globalConfig.languageCode) {
					const prefixedKeys = this.getAllByPrefix(PREFIX_LANGUAGE);
					keysToPurge.push(...prefixedKeys);
					obs$.push(this.appCache.setItemAsObservable(CACHE_KEY_LANGUAGE, this.globalConfig.languageCode));
				}

				if (!obs$.length) {
					return of(undefined);
				}

				return combineLatest(obs$).pipe(switchMapTo(this.remove(keysToPurge)));
			})
		);
	}

	private keys(): CacheKey[] {
		// when third-party script cookies are disallowed, access to localStorage may result in thrown SecurityError exceptions
		const { localStorage } = this.windowRef?.native;
		if (localStorage) {
			const indexDbKeys = this.cacheFactory.getIndexDBKeys().map((name) => ({
				name,
				storageMode: StorageMode.IndexedDBStorage,
			}));
			const localStorageKeys = Object.keys(localStorage).map((name) => ({
				name,
				storageMode: StorageMode.LocalStorage,
			}));
			return [...localStorageKeys, ...indexDbKeys];
		}
		return [];
	}

	private removeIndexDbByKey(key: string) {
		const indexedDb = this.windowRef.native.indexedDB;
		return new Promise((resolve, reject) => {
			const transaction = indexedDb.deleteDatabase(key);
			transaction.onsuccess = resolve;
			transaction.onerror = reject;
			transaction.onblocked = reject;
			transaction.onupgradeneeded = reject;
		});
	}

	private async remove(keys: CacheKey[] | CacheKey): Promise<void> {
		if (isEmpty(keys)) {
			return;
		}
		const removeItems = [];
		for (const key of castArray(keys)) {
			if (key.storageMode === StorageMode.IndexedDBStorage) {
				removeItems.push(this.removeIndexDbByKey(key.name));
				continue;
			}
			this.windowRef.native.localStorage.removeItem(key.name);
		}

		await Promise.all(removeItems);
	}

	private getAllByPrefix(prefix: string | null): CacheKey[] {
		const cacheKeys: CacheKey[] = [];
		const storageKeys = this.keys();
		for (const key of storageKeys) {
			if ((key && !key.name.startsWith(PREFIX_DEFAULT)) || !includes(key.name, `.${prefix}`)) {
				continue;
			}

			if (includes(cacheKeys, key)) {
				continue;
			}

			cacheKeys.push(key);
		}

		return cacheKeys;
	}
}
